import React, { useState, useRef, useEffect } from 'react';

import { motion, AnimatePresence } from "framer-motion";
import axios from 'axios';

function Device({ darkMode }) {
  const [devices, setDevices] = useState([]);
  const [currentApi, setCurrentApi] = useState('SMS-Activate');
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingBalance, setEditingBalance] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [newBalance, setNewBalance] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const dropdownRef = useRef(null);

// 在組件頂部新增狀態
const [modalMessage, setModalMessage] = useState({
  show: false,
  text: '',
  isError: false
});

// 修改 showMessage 函數
const showMessage = (text, isError = false) => {
  setModalMessage({
    show: true,
    text,
    isError
  });
};

// 新增關閉模態框的函數
const closeModal = () => {
  setModalMessage({
    show: false,
    text: '',
    isError: false
  });
};

// ConfirmModal 組件
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, darkMode }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black opacity-30"></div>
      <div className={`relative px-6 py-4 rounded-lg shadow-lg ${
        darkMode 
          ? 'bg-[#1E1E1E] border-2 border-[#404040]' 
          : 'bg-white border-2 border-[#1E1E1E]'
      }`}>
        <div className="flex flex-col items-center space-y-3">
          <i className="fas fa-question-circle text-2xl text-[#f59e0b]"></i>
          
          <h3 className={`text-base font-medium ${
            darkMode ? 'text-[#FFFFFF]' : 'text-[#1E1E1E]'
          }`}>
            {title}
          </h3>
          
          <p className={`text-sm text-center ${
            darkMode ? 'text-[#FFFFFF]/80' : 'text-[#1E1E1E]/80'
          }`}>
            {message}
          </p>

          <div className="flex space-x-3 mt-4">
            <button
              onClick={onConfirm}
              className={`px-4 py-2 rounded-lg text-sm transition-colors duration-200 ${
                darkMode
                  ? 'bg-green-900 text-green-400 hover:bg-green-800'
                  : 'bg-green-100 text-green-600 hover:bg-green-200'
              }`}
            >
              確認
            </button>
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-sm transition-colors duration-200 ${
                darkMode
                  ? 'bg-red-900 text-red-400 hover:bg-red-800'
                  : 'bg-red-100 text-red-600 hover:bg-red-200'
              }`}
            >
              取消
            </button>
            
          </div>
        </div>
      </div>
    </div>
  );
};


// 在主組件中
const [modalConfig, setModalConfig] = useState({
  isOpen: false,
  action: null,
  deviceId: null,
  deviceName: null,
  title: '',
  message: ''
});

const getModalConfig = (action, deviceName) => {
  switch (action) {
    case 'uberDevice':
      return {
        title: '重設 Uber',
        message: `確定要重設 ${deviceName} 的 Uber 嗎？`
      };
    case 'resetDevice':
      return {
        title: '自動重設',
        message: `確定要自動重設 ${deviceName} 嗎？`
      };
    case 'buttonDevice':
      return {
        title: '支付寶密碼',
        message: `確定要自動輸入 ${deviceName} 的支付寶密碼嗎？`
      };
    default:
      return { title: '', message: '' };
  }
};

const handleModalOpen = (action, deviceId, deviceName) => {
  const config = getModalConfig(action, deviceName);
  setModalConfig({
    isOpen: true,
    action,
    deviceId,
    deviceName,
    ...config
  });
};



  const statusOptions = [
    { value: 'active', label: '正常' },
    { value: 'inactive', label: '已停用' },
    { value: 'stopped', label: '已強制停止' }
  ];

  const fetchDevices = async () => {
    try {
      const response = await fetch('https://xber.hk/api/devices', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setDevices(data);
      setLastUpdateTime(Date.now());
    } catch (error) {
      console.error('獲取設備列表失敗:', error);
    }
  };

  const handleApiChange = async (newApi) => {
    try {
      setIsLoading(true);
      const response = await fetch('https://xber.hk/api/change-api', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiId: newApi === "SMS-Activate" ? "0" : "1"
        })
      });
      
      if (response.ok) {
        setCurrentApi(newApi);
        showMessage(`成功切換至 ${newApi}`);
      }
    } catch (error) {
      showMessage("API切換失敗", true);
    } finally {
      setIsLoading(false);
    }
  };
  
const handleReload = async () => {
  try {
    setIsLoading(true);
    const response = await fetch('https://vysor.querymail.net/api/vysor/reload', {
      method: 'GET',
      headers: {
		'Accept': 'application/json',
		'Content-Type': 'application/json',
		'Cache-Control': 'no-cache'
      }
    });
	const data = await response.json();
    if (data.status === 'success') {
      showMessage('重新載入成功');
    }
  } catch (error) {
    showMessage("重新載入失敗", true);
  } finally {
    setIsLoading(false);
  }
};


const handleDeviceAction = async (action, deviceID, deviceName) => {
  try {
    setIsLoading(true);
    
    const response = await fetch(`https://xber.hk/api/${action}?serial=${deviceID}`, {
      method: 'GET'
    });

    if (response.ok) {
      showMessage(`${getActionName(action)} ${deviceName} 成功`);
      fetchDevices();
    } else {
      const data = await response.json();
      showMessage(data.message || `${getActionName(action)}失敗`, true);
    }

  } catch (error) {
    showMessage(`${getActionName(action)}失敗`, true);
    console.error('Error:', error);
  } finally {
    setIsLoading(false);
  }
};




// 然後在處理餘額變更時
const handleBalanceChange = async (deviceID) => {
  if (!newBalance || isNaN(parseFloat(newBalance))) {
    showMessage('請輸入有效的餘額', true);
    return;
  }

  try {
	// 使用方式
    const response = await fetch('https://xber.hk/api/alipayDefaultBalance', {
      method: 'POST',
      credentials: 'include',  // 重要：包含憑證
      headers: {
        'Content-Type': 'application/json',
		'Accept': 'application/json'
      },
      body: JSON.stringify({
        deviceID: deviceID,
        price: parseFloat(newBalance)
      })
    });

    if (response.status === 200) {
      const data = await response.json();
      showMessage(data.message || '餘額更新成功');
      setEditingBalance(null);
      fetchDevices();
    } else if (response.status === 401) {
      // 如果未授權，嘗試重新登入
      const loginSuccess = await login();
      if (loginSuccess) {
        // 重試原始請求
        handleBalanceChange(deviceID);
      } else {
        showMessage('請先登入', true);
      }
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || '餘額更新失敗';
    showMessage(errorMessage, true);
    console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
    });
  }
};

const handleStatusChange = async (deviceID, selectedStatus) => {
	const validStatuses = ['inactive', 'active', 'stopped'];
    if (!validStatuses.includes(selectedStatus)) {
        showMessage('無效的狀態選擇', true);
        return;
    }

  try {
    setIsLoading(true);

    let apiEndpoint = '';
    switch(selectedStatus) { // 使用傳入的 selectedStatus 而不是 newStatus
      case 'inactive':
        apiEndpoint = `https://xber.hk/api/inactiveDevice?serial=${deviceID}`;
        break;
      case 'active':
        apiEndpoint = `https://xber.hk/api/activeDevice?serial=${deviceID}`;
        break;
      case 'stopped':
        apiEndpoint = `https://xber.hk/api/stopDevice?serial=${deviceID}`;
        break;
      default:
        showMessage('無效的狀態', true);
        return;
    }

    const response = await fetch(apiEndpoint, {
      method: 'GET',
      credentials: 'include',
    });

    if (response.ok) {
      showMessage('狀態更新成功');
      setEditingStatus(null);
      fetchDevices();
    } else {
      const data = await response.json();
      showMessage(data.message || '狀態更新失敗', true);
    }
  } catch (error) {
    showMessage('狀態更新失敗', true);
    console.error('Error:', error);
  } finally {
    setIsLoading(false);
  }
};

  const handleEditBalance = (deviceID, currentBalance) => {
    setEditingBalance(deviceID);
    setNewBalance("");
  };

  const handleEditStatus = (deviceID, currentStatus) => {
   if (currentStatus === 'alipaying' || currentStatus === 'ubering') {
     return; // 移除這個限制
   }
	  
    setEditingStatus(deviceID);
    setNewStatus(currentStatus);
  };

  const getActionName = (action) => ({
    resetDevice: '自動重設',
    uberDevice: '重設 Uber',
    powerOff: '電源開關',
    buttonDevice: '支付寶密碼操作',
    activeDevice: '啟用',
    inactiveDevice: '停用',
    stopDevice: '強制停止'
  }[action] || action);

  const getStatusInfo = (status) => {
    switch(status) {
      case 'active':
        return {
          icon: 'fas fa-circle-check',
          text: '待機中',
          color: darkMode ? 'text-green-400' : 'text-green-600',
          bgColor: darkMode ? 'bg-green-900/20' : 'bg-green-100',
          progressColor: darkMode ? 'bg-green-400' : 'bg-green-600'
        };
      case 'inactive':
        return {
          icon: 'fas fa-circle-xmark',
          text: '已停用',
          color: darkMode ? 'text-red-400' : 'text-red-600',
          bgColor: darkMode ? 'bg-red-900/20' : 'bg-red-100',
          progressColor: darkMode ? 'bg-red-400' : 'bg-red-600'
        };
      case 'stopped':
        return {
          icon: 'fas fa-circle-pause',
          text: '已強制停止',
          color: darkMode ? 'text-yellow-400' : 'text-yellow-600',
          bgColor: darkMode ? 'bg-yellow-900/20' : 'bg-yellow-100',
          progressColor: darkMode ? 'bg-yellow-400' : 'bg-yellow-600'
        };
      case 'ubering':
        return {
          icon: 'fab fa-uber',
          text: '重設中',
			color: darkMode ? 'text-[#FFFFFF]' : 'text-[#1E1E1E]', // 深色模式用偏白色，淺色模式用深褐色
			bgColor: darkMode ? 'bg-[#FFFFFF]/20' : 'bg-[#404040]/20', // 背景使用對應顏色的半透明版本
			progressColor: darkMode ? 'bg-[#FFFFFF]' : 'bg-[#404040]' // 進度條顏色
        };
      case 'alipaying':
        return {
          icon: 'fab fa-alipay',
          text: '重設中',
			color: darkMode ? 'text-[#69A9FF]' : 'text-[#1677FF]', // 深色模式用中等亮度的藍色
			bgColor: darkMode ? 'bg-[#69A9FF]/20' : 'bg-[#1677FF]/10',
			progressColor: darkMode ? 'bg-[#69A9FF]' : 'bg-[#1677FF]'
        };
      default:
        return {
          icon: 'fas fa-question',
          text: '未知',
          color: darkMode ? 'text-gray-400' : 'text-gray-600',
          bgColor: darkMode ? 'bg-gray-900/20' : 'bg-gray-100',
          progressColor: darkMode ? 'bg-gray-400' : 'bg-gray-600'
        };
    }
  };
  
const fetchCurrentApi = () => {
    fetch("https://xber.hk/api/current-api", {
        method: "GET"
    })
    .then((response) => {
        if (!response.ok) {
            throw new Error("無法獲取當前 API，伺服器返回錯誤：" + response.status);
        }
        return response.text();
    })
    .then((apiName) => {
        // 使用 setCurrentApi 更新狀態
        if (apiName === "sms-activate") {
            setCurrentApi("SMS-Activate");
        } else if (apiName === "robot") {
            setCurrentApi("Robot-SMS");
        } else {
            console.warn("未知的 API 名稱：", apiName);
            setCurrentApi("Unknown API");
        }
        
        // 如果仍需要更新下拉選單
        const apiSelector = document.getElementById("api-selector");
        if (apiSelector) {
            apiSelector.value = apiName === "sms-activate" ? "0" : "1";
        }
    })
    .catch((error) => {
        console.error("無法獲取當前 API：", error);
        setCurrentApi("Error Loading API");
    });
};



useEffect(() => {
    const handleClickOutside = (event) => {
        if (!dropdownRef.current?.contains(event.target)) {
            const isDropdownTrigger = event.target.closest('[data-dropdown-trigger]');
            const targetTag = event.target.tagName.toLowerCase();
            const shouldExcludeReset = event.target.closest('[data-exclude-reset]');
            
            if (!isDropdownTrigger && 
                !['select', 'input'].includes(targetTag) && 
                !shouldExcludeReset) {
                resetEditing();
            }
        }
    };

    const resetEditing = () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
        setEditingStatus(null);
        setEditingBalance(null);
        setIsOpen(false);
    };

    if (editingBalance || editingStatus || isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }
}, [isOpen, editingBalance, editingStatus]);






useEffect(() => {
    let isSubscribed = true;
    
    const fetchData = async () => {
        try {
            if (!isSubscribed) return;
            
            const [devicesResponse, apiResponse] = await Promise.all([
                fetchDevices(),
                fetchCurrentApi()
            ]);
            
            if (!isSubscribed) return;
            
            // 更新狀態
        } catch (error) {
            if (!isSubscribed) return;
            showMessage('數據更新失敗', true);
        }
    };

    fetchData();
    const interval = setInterval(fetchData, 3000);
    
    return () => {
        isSubscribed = false;
        clearInterval(interval);
    };
}, []);

  return (
    <>
<AnimatePresence>
  {isLoading && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className={`fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 ${
        darkMode ? "bg-black/50" : "bg-white/50"
      }`}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="relative"
      >
        {/* 主要載入動畫 */}
        <div className="relative flex items-center justify-center">
          {/* 外圈脈衝 */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.2, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className={`absolute w-12 h-12 rounded-full ${
              darkMode ? "bg-white/10" : "bg-black/10"
            }`}
          />
          
          {/* 旋轉元素 */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 1.5,
              ease: "linear",
              repeat: Infinity
            }}
            className={`w-8 h-8 rounded-full border-2 border-t-2 ${
              darkMode 
                ? "border-white/20 border-t-white" 
                : "border-black/20 border-t-black"
            }`}
          />
        </div>

        {/* 底部文字 */}
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`mt-4 text-center ${
            darkMode ? "text-white/70" : "text-black/70"
          }`}
        >
          <div className="text-sm font-medium">載入中</div>
          <div className="text-xs mt-1 opacity-60">請稍候...</div>
        </motion.div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

	
	
	
    <AnimatePresence>

      
	  
      <motion.div 
        className={`p-8 rounded-xl shadow-lg ${darkMode ? "bg-[#1E1E1E]" : "bg-white"}`}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
		
{/* 模態框 */}
    {modalMessage.show && (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="fixed inset-0 bg-black opacity-30"></div>
        <div className={`relative px-6 py-4 rounded-lg shadow-lg ${
          darkMode 
            ? 'bg-[#1E1E1E] border-2 border-[#404040]' 
            : 'bg-white border-2 border-[#1E1E1E]'
        }`}>
          <div className="flex flex-col items-center space-y-3">
            <i className={`text-2xl ${
              modalMessage.isError 
                ? 'fas fa-exclamation-circle text-red-500' 
                : 'fas fa-check-circle text-green-500'
            }`}></i>
            <p className={`text-sm ${
              darkMode ? 'text-[#FFFFFF]' : 'text-[#1E1E1E]'
            }`}>
              {modalMessage.text}
            </p>
            <button
              onClick={closeModal}
              className={`mt-4 px-4 py-2 rounded-lg text-sm transition-colors duration-200 ${
                darkMode
                  ? 'bg-[#404040] text-[#FFFFFF] hover:bg-[#5e5e5e]'
                  : 'bg-[#1E1E1E] text-white hover:bg-[#1E1E1E]'
              }`}
            >
              確定
            </button>
          </div>
        </div>
      </div>
    )}
	
<ConfirmModal
  isOpen={modalConfig.isOpen}
  onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
  onConfirm={() => {
    handleDeviceAction(modalConfig.action, modalConfig.deviceId, modalConfig.deviceName);
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  }}
  title={modalConfig.title}
  message={modalConfig.message}
  darkMode={darkMode}
/>

        <div className="mb-8">
  <h2 className={`text-xl md:text-2xl font-bold mb-4 md:mb-6 ${
    darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
  }`}>
    設備控制面板
  </h2>
          
  <div className={`p-4 md:p-6 rounded-xl backdrop-blur-sm ${
    darkMode ? "bg-[#2C2C2C]" : "bg-[#F5F5F5]"
  } transition-all duration-300`}>
    <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
      {/* API 服務選擇區 */}
      <div className={`flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4`}>
        <span className={`text-base md:text-lg font-semibold ${
          darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
        }`}>
          API 服務
        </span>
        
        <div className="relative">
          <select 
            className={`appearance-none w-full md:w-48 px-4 md:px-6 py-2.5 md:py-3 pr-12 rounded-lg border-2 font-medium transition-all duration-200 outline-none ${
              darkMode 
                ? "bg-[#1E1E1E] text-[#FFFFFF] border-[#404040] hover:border-[#FFFFFF]" 
                : "bg-white text-[#1E1E1E] border-[#1E1E1E] hover:border-[#404040]"
            }`}
            value={currentApi}
            onChange={(e) => handleApiChange(e.target.value)}
          >
            <option value="SMS-Activate">SMS-Activate</option>
            <option value="Robot-SMS">Robot-SMS</option>
          </select>
          
          <div className={`absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none ${
            darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
          }`}>
            <i className="fas fa-chevron-down text-base md:text-lg"></i>
          </div>
        </div>
      </div>
	  
  <button
    onClick={handleReload}
    disabled={isLoading}
    className={`px-4 py-2.5 rounded-lg border-2 font-medium transition-all duration-200 ${
      darkMode 
        ? "bg-[#1E1E1E] text-[#FFFFFF] border-[#404040] hover:border-[#FFFFFF]" 
        : "bg-white text-[#1E1E1E] border-[#1E1E1E] hover:border-[#404040]"
    }`}
  >
     <i className="fas fa-sync-alt"></i> 重置 Vysor
  </button>
              
      {/* 當前服務顯示區 */}
      <div className={`px-4 md:px-6 py-2 rounded-lg font-semibold text-center md:text-left`}>
        <span className={`text-xs md:text-sm ${
          darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
        }`}>
          當前選擇服務: {' '}
          <span className="font-bold text-sm md:text-base">
            {currentApi}
          </span>
        </span>
      </div>
    </div>
  </div>
</div>


<div className={`rounded-xl hidden md:block overflow-hidden border ${
  darkMode 
    ? "border-[#2C2C2C]" // 深色模式的邊框顏色
    : "border-[#F5F5F5]" // 淺色模式的邊框顏色
}`}>
          <table className="w-full">
            <thead className={`${darkMode ? "bg-[#2C2C2C]" : "bg-[#F5F5F5]"}`}>
              <tr>
                <th scope="col" className={`px-6 py-3 text-center text-sm font-semibold ${
                  darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
                }`}>API</th>
                <th scope="col" className={`px-6 py-3 text-center text-sm font-semibold ${
                  darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
                }`}>設備名稱</th>
                <th scope="col" className={`px-12 py-3 text-left text-sm font-semibold ${
                  darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
                }`}>狀態</th>
                <th scope="col" className={`px-12 py-3 text-left text-sm font-semibold ${
                  darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
                }`}>餘額</th>
                <th scope="col" className={`px-16 py-3 text-left text-sm font-semibold ${
                  darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
                }`}>操作</th>
              </tr>
            </thead>
            <tbody className={`${darkMode ? "bg-[#1E1E1E]" : "bg-white"} divide-y ${
              darkMode ? "divide-[#2C2C2C]" : "divide-[#F5F5F5]"
            }`}>
              {devices.map((device) => {
                const statusInfo = getStatusInfo(device.deviceStatus);
                const isResetting = ['alipaying', 'ubering'].includes(device.deviceStatus);
                return (
                  <tr key={device.deviceID}>
                    <td className="px-6 py-3 text-center">
                      <span className={`text-sm ${
                        darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
                      }`}>
                        {device.api === 0 ? 'SMS-Activate' : 'Robot-SMS'}
                      </span>
                    </td>

					<td className="px-6 py-3 text-center">  {/* 改用 text-center */}
					  <div className="flex items-center justify-center space-x-4"> {/* 添加 justify-center */}
                        <div className="content-center">
                          {device.power === 1 && (
                            <div className={`absolute w-4 h-4 -z-10 rounded-full ${
                              darkMode ? 'bg-green-400' : 'bg-green-500'
                            } opacity-20 blur-[2px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`} />
                          )}
<button
  key={`power-btn-${device.deviceID}`}
  onClick={() => handleDeviceAction('powerOff', device.deviceID, device.deviceName)}
  disabled={['alipaying', 'ubering'].includes(device.deviceStatus)}
  className={`z-10 transition-colors duration-200 ${
    ['alipaying', 'ubering'].includes(device.deviceStatus)
      ? darkMode 
        ? 'text-red-400 cursor-not-allowed' 
        : 'text-red-600 cursor-not-allowed'
    : device.power === 1 
      ? darkMode 
        ? 'text-green-400' 
        : 'text-green-500'
      : 'text-gray-400'
  }`}
>
  <i className="fas fa-power-off" />
</button>

                        </div>
                        <span className={`text-sm font-medium ${
                          darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"
                        }`}>
                          {device.deviceName}
                        </span>
                      </div>
                    </td>

<td className="px-6 py-3 text-left">
  		{editingStatus === device.deviceID ? (
    <div ref={dropdownRef} className="relative" key={`status-edit-${device.deviceID}`}>
      <button
		data-dropdown-trigger="true"
        onClick={() => setIsOpen(!isOpen)}
		disabled={['alipaying', 'ubering'].includes(device.deviceStatus)} 
        className={`flex items-center justify-between w-[150px] px-3 py-1.5 text-sm rounded-lg border-2 ${
          darkMode
            ? "bg-[#1E1E1E] text-[#FFFFFF] border-[#404040] hover:bg-[#2C2C2C]"
            : "bg-white text-[#1E1E1E] border-[#1E1E1E] hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center space-x-2">
          <i className={`${getStatusInfo(newStatus).icon} ${getStatusInfo(newStatus).color} text-base`}></i>
          <span className={`${getStatusInfo(newStatus).color} font-medium`}>{getStatusInfo(newStatus).text}</span>
        </div>
        <i className="fas fa-chevron-down ml-2 text-sm"></i>
      </button>

      {isOpen && (
		<div className={`absolute z-10 mt-1 w-[150px] rounded-lg border-2 shadow-lg overflow-hidden ${
		  darkMode 
			? "bg-[#1E1E1E] border-[#404040]" 
			: "bg-white border-[#1E1E1E]"
		}`}>
		  {['active', 'inactive', 'stopped'].map((status, index, array) => {
			const statusInfo = getStatusInfo(status);
			return (
			  <div
				key={status}
				data-exclude-reset="true" // 添加這個屬性
				onClick={() => {
				  const selectedStatus = status; // 先保存選中的狀態
				  setNewStatus(selectedStatus);
				  setIsOpen(false);
				  handleStatusChange(device.deviceID, selectedStatus); // 直接傳入選中的狀態
				  setEditingStatus(null); // 關閉編輯狀態
				}}
				className={`flex items-start px-3 py-1.5 cursor-pointer
				  ${statusInfo.bgColor}
				  ${index === 0 ? 'rounded-t-lg' : ''} 
				  ${index === array.length - 1 ? 'rounded-b-lg' : ''}
				  hover:brightness-95
				`}
			  >
				<div className="flex items-center space-x-2">
				  <i className={`${statusInfo.icon} ${statusInfo.color} text-sm`}></i>
				  <span className={`${statusInfo.color} text-sm font-medium`}>
					{statusInfo.text}
				  </span>
				</div>
			  </div>
			);
		  })}
		</div>
      )}
    </div>
  ) : (
	  <div 
		onClick={() => !['alipaying', 'ubering'].includes(device.deviceStatus) && handleEditStatus(device.deviceID, device.deviceStatus)}
		className={`inline-flex items-center space-x-2 ${['alipaying', 'ubering'].includes(device.deviceStatus) ? 'cursor-not-allowed' : 'cursor-pointer'} px-3 py-1.5 rounded-lg ${statusInfo.bgColor}`}
	  >
      <i className={`${statusInfo.icon} ${statusInfo.color}`}></i>
      <span className={`text-sm font-medium ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
      {isResetting && (
        <div className="space-y-1 mt-1">
          <div className={`h-1.5 rounded-full overflow-hidden ${
            darkMode ? "bg-black/20" : "bg-white/60"
          }`}>
            <div
              className={`h-full rounded-full transition-all duration-300 ${statusInfo.progressColor}`}
              style={{ width: `${device.progress || 0}%` }}
            />
          </div>
          <div className={`text-xs ${statusInfo.color} opacity-80`}>
            {device.progress || 0}%
          </div>
        </div>
      )}
    </div>
  )}
</td>



                    <td className="px-6 py-3 text-left">
{editingBalance === device.deviceID ? (
  <div ref={dropdownRef} className="flex items-center space-x-2" key={`balance-edit-${device.deviceID}`}>
    <motion.div 
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
    >
      <div className="flex-shrink-0">
        <input
          type="number"
          value={newBalance}
          onChange={(e) => setNewBalance(e.target.value)}
          onClick={(e) => e.stopPropagation()} // 阻止事件冒泡
          className={`w-16 px-3 py-1 text-sm rounded-lg border-2 transition-all duration-200 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            darkMode
              ? "bg-[#1E1E1E] text-[#FFFFFF] border-[#404040] focus:border-[#FFFFFF]"
              : "bg-white text-[#1E1E1E] border-[#1E1E1E] focus:border-[#404040]"
          } ${
            ['alipaying', 'ubering'].includes(device.deviceStatus)
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:border-opacity-75'
          }`}
          step="0.01"
          disabled={['alipaying', 'ubering'].includes(device.deviceStatus)}
        />
      </div>

      <motion.button
		data-exclude-reset="true"
        initial={{ opacity: 0, rotate: -180, scale: 0.9 }}
        animate={{ opacity: 1, rotate: 0, scale: 1 }}
        transition={{ 
          duration: 0.1,
          delay: 0.1,
          ease: "easeOut"
        }}
        onClick={(e) => {
          e.stopPropagation(); // 阻止事件冒泡
          if (!['alipaying', 'ubering'].includes(device.deviceStatus)) {
            handleBalanceChange(device.deviceID)
          }
        }}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
          ['alipaying', 'ubering'].includes(device.deviceStatus)
            ? darkMode
              ? 'bg-[#2C2C2C] text-[#666666] cursor-not-allowed'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : parseFloat(device.balance) === 0
              ? darkMode
                ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50 cursor-pointer'
                : 'bg-green-100 text-green-600 hover:bg-green-200 cursor-pointer'
              : darkMode
                ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50 cursor-pointer'
                : 'bg-green-100 text-green-600 hover:bg-green-200 cursor-pointer'
        }`}
        whileHover={!['alipaying', 'ubering'].includes(device.deviceStatus) ? {
          scale: 1.05,
          transition: { duration: 0.2 }
        } : {}}
        whileTap={!['alipaying', 'ubering'].includes(device.deviceStatus) ? {
          scale: 0.95,
          transition: { duration: 0.2 }
        } : {}}
      >
        <motion.i 
          className="fas fa-check"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            duration: 0.1,
            delay: 0.1,
            ease: "backOut"
          }}
        />
      </motion.button>
    </motion.div>
  </div>
) : (
  <motion.div
    onClick={() => {
      if (!['alipaying', 'ubering'].includes(device.deviceStatus)) {
        handleEditBalance(device.deviceID, device.balance)
      }
    }}
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
    className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all duration-200 ${
      ['alipaying', 'ubering'].includes(device.deviceStatus)
        ? darkMode
          ? 'bg-[#2C2C2C] text-[#666666] cursor-not-allowed'
          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        : parseFloat(device.balance) === 0
          ? darkMode
            ? 'bg-gray-900/30 text-gray-400 hover:bg-gray-700/50 cursor-pointer'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer'
          : darkMode
            ? 'bg-green-900/30 text-green-400 hover:bg-green-700/50 cursor-pointer'
            : 'bg-green-100 text-green-600 hover:bg-green-200 cursor-pointer'
    }`}
    whileHover={!['alipaying', 'ubering'].includes(device.deviceStatus) ? {
      scale: 1.02,
      transition: { duration: 0.2 }
    } : {}}
    whileTap={!['alipaying', 'ubering'].includes(device.deviceStatus) ? {
      scale: 0.98,
      transition: { duration: 0.2 }
    } : {}}
  >
    <motion.i 
      className="fas fa-wallet"
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ 
        duration: 0.3,
        delay: 0.1,
        ease: "backOut"
      }}
    />
    <motion.span 
      className="text-sm font-medium"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ 
        duration: 0.3,
        delay: 0.2,
        ease: "easeOut"
      }}
    >
      {parseFloat(device.balance).toFixed(2)}
    </motion.span>
  </motion.div>
)}


                    </td>
<td className="px-6 py-3">
  <div className="flex items-center justify-left space-x-2">
    {isResetting ? (
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`group relative px-3 py-2 rounded-lg transition-all duration-200 ${
          darkMode 
            ? 'bg-yellow-900 text-yellow-400 hover:bg-yellow-800' 
            : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
        }`}
        onClick={() => handleDeviceAction('stopDevice', device.deviceID, device.deviceName)}
      >
        <i className="fas fa-stop"></i>
        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded bg-black text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
          強制停止
        </span>
      </motion.button>
    ) : (
      <>
        <motion.button
          whileHover={device.deviceStatus !== 'inactive' ? { scale: 1.05 } : {}}
          whileTap={device.deviceStatus !== 'inactive' ? { scale: 0.95 } : {}}
          disabled={device.deviceStatus === 'inactive'}
          className={`group relative px-3 py-2 rounded-lg transition-all duration-200 ${
            darkMode 
              ? device.deviceStatus === 'inactive'
                ? 'bg-[#2C2C2C] cursor-not-allowed'
                : 'bg-[#404040] hover:bg-[#2C2C2C]'
              : device.deviceStatus === 'inactive'
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gray-600 hover:bg-gray-400'
          }`}
          onClick={() => device.deviceStatus !== 'inactive' && handleModalOpen('uberDevice', device.deviceID, device.deviceName)}
        >
          <i className={`fab fa-uber ${
            darkMode 
              ? device.deviceStatus === 'inactive' ? 'text-[#666666]' : 'text-[#FFFFFF]'
              : device.deviceStatus === 'inactive' ? 'text-gray-300' : 'text-white'
          }`}></i>
          {device.deviceStatus !== 'inactive' && (
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded bg-black text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
              重設 Uber
            </span>
          )}
        </motion.button>

        <motion.button
          whileHover={device.deviceStatus !== 'inactive' ? { scale: 1.05 } : {}}
          whileTap={device.deviceStatus !== 'inactive' ? { scale: 0.95 } : {}}
          disabled={device.deviceStatus === 'inactive'}
          className={`group relative px-3 py-2 rounded-lg transition-all duration-200 ${
            darkMode 
              ? device.deviceStatus === 'inactive'
                ? 'bg-[#2C2C2C] cursor-not-allowed'
                : 'bg-[#404040] hover:bg-[#2C2C2C]'
              : device.deviceStatus === 'inactive'
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gray-600 hover:bg-gray-400'
          }`}
          onClick={() => device.deviceStatus !== 'inactive' && handleModalOpen('resetDevice', device.deviceID, device.deviceName)}
        >
          <i className={`fas fa-redo ${
            darkMode 
              ? device.deviceStatus === 'inactive' ? 'text-[#666666]' : 'text-[#FFFFFF]'
              : device.deviceStatus === 'inactive' ? 'text-gray-300' : 'text-white'
          }`}></i>
          {device.deviceStatus !== 'inactive' && (
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded bg-black text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
              自動重設
            </span>
          )}
        </motion.button>

        <motion.button
          whileHover={device.deviceStatus !== 'inactive' ? { scale: 1.05 } : {}}
          whileTap={device.deviceStatus !== 'inactive' ? { scale: 0.95 } : {}}
          disabled={device.deviceStatus === 'inactive'}
          className={`group relative px-3 py-2 rounded-lg transition-all duration-200 ${
            darkMode 
              ? device.deviceStatus === 'inactive'
                ? 'bg-[#2C2C2C] cursor-not-allowed'
                : 'bg-[#404040] hover:bg-[#2C2C2C]'
              : device.deviceStatus === 'inactive'
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gray-600 hover:bg-gray-400'
          }`}
          onClick={() => device.deviceStatus !== 'inactive' && handleModalOpen('buttonDevice', device.deviceID, device.deviceName)}
        >
          <i className={`fas fa-key ${
            darkMode 
              ? device.deviceStatus === 'inactive' ? 'text-[#666666]' : 'text-[#FFFFFF]'
              : device.deviceStatus === 'inactive' ? 'text-gray-300' : 'text-white'
          }`}></i>
          {device.deviceStatus !== 'inactive' && (
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded bg-black text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
              支付寶密碼
            </span>
          )}
        </motion.button>
      </>
    )}
  </div>
</td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>
	  
{/* 手機版卡片 - md尺寸以下顯示 */}
<div className="block md:hidden space-y-4 mt-4">
  {devices.map((device) => {
    const statusInfo = getStatusInfo(device.deviceStatus);
    const isResetting = ['alipaying', 'ubering'].includes(device.deviceStatus);
    
    return (
      <motion.div
        key={device.deviceID}
        className={`p-4 rounded-xl shadow-lg ${darkMode ? "bg-[#1E1E1E]" : "bg-[#FFFFFF]"}`}
      >
        {/* API & 設備名稱 */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
<button
  onClick={() => handleDeviceAction('powerOff', device.deviceID, device.deviceName)}
  disabled={['alipaying', 'ubering'].includes(device.deviceStatus)}
  className={`z-10 transition-colors duration-200 ${
    ['alipaying', 'ubering'].includes(device.deviceStatus)
      ? darkMode 
        ? 'text-red-400 cursor-not-allowed' 
        : 'text-red-600 cursor-not-allowed'
    : device.power === 1 
      ? darkMode 
        ? 'text-green-400' 
        : 'text-green-500'
      : 'text-gray-400'
  }`}
>
  <i className="fas fa-power-off" />
</button>

            <span className={`text-sm font-medium ${darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"}`}>
              {device.deviceName}
            </span>
          </div>
          <span className={`text-sm ${darkMode ? "text-[#FFFFFF]" : "text-[#1E1E1E]"}`}>
            {device.api === 0 ? 'SMS-Activate' : 'Robot-SMS'}
          </span>
        </div>

        {/* 狀態和餘額 */}
        <div className="flex flex-wrap gap-2 mb-2" key={`device-status-${device.deviceID}`}>
		
		
		
  		{editingStatus === device.deviceID ? (
    <div ref={dropdownRef} className="relative" key={`status-edit-${device.deviceID}`}>
      <button
		data-dropdown-trigger="true"
        onClick={() => setIsOpen(!isOpen)}
		disabled={['alipaying', 'ubering'].includes(device.deviceStatus)}
        className={`flex items-center justify-between w-[120px] px-3 py-1.5 text-sm rounded-lg border-2 ${
          darkMode
            ? "bg-[#1E1E1E] text-[#FFFFFF] border-[#404040] hover:bg-[#2C2C2C]"
            : "bg-white text-[#1E1E1E] border-[#1E1E1E] hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center space-x-2">
          <i className={`${getStatusInfo(newStatus).icon} ${getStatusInfo(newStatus).color} text-base`}></i>
          <span className={`${getStatusInfo(newStatus).color} font-medium`}>{getStatusInfo(newStatus).text}</span>
        </div>
        <i className="fas fa-chevron-down ml-2 text-sm"></i>
      </button>

      {isOpen && (
		<div className={`absolute z-10 mt-1 w-fit rounded-lg border-2 shadow-lg overflow-hidden ${
		  darkMode 
			? "bg-[#1E1E1E] border-[#404040]" 
			: "bg-white border-[#1E1E1E]"
		}`}>
		  {['active', 'inactive', 'stopped'].map((status, index, array) => {
			const statusInfo = getStatusInfo(status);
			return (
			  <div
				key={status}
				data-exclude-reset="true"
				onClick={() => {
				  const selectedStatus = status; // 先保存選中的狀態
				  setNewStatus(selectedStatus);
				  setIsOpen(false);
				  handleStatusChange(device.deviceID, selectedStatus); // 直接傳入選中的狀態
				  setEditingStatus(null); // 關閉編輯狀態
				}}
				className={`flex items-start px-3 py-1.5 cursor-pointer
				  ${statusInfo.bgColor}
				  ${index === 0 ? 'rounded-t-lg' : ''} 
				  ${index === array.length - 1 ? 'rounded-b-lg' : ''}
				  hover:brightness-95
				`}
			  >
				<div className="flex items-center space-x-2">
				  <i className={`${statusInfo.icon} ${statusInfo.color} text-sm`}></i>
				  <span className={`${statusInfo.color} text-sm font-medium`}>
					{statusInfo.text}
				  </span>
				</div>
			  </div>
			);
		  })}
		</div>
      )}
    </div>
  ) : (
	  <div 
		onClick={() => !['alipaying', 'ubering'].includes(device.deviceStatus) && handleEditStatus(device.deviceID, device.deviceStatus)}
		className={`inline-flex items-center space-x-2 ${['alipaying', 'ubering'].includes(device.deviceStatus) ? 'cursor-not-allowed' : 'cursor-pointer'} px-3 py-1.5 rounded-lg ${statusInfo.bgColor}`}
	  >
      <i className={`${statusInfo.icon} ${statusInfo.color}`}></i>
      <span className={`text-sm font-medium ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
      {isResetting && (
        <div className="space-y-1 mt-1">
          <div className={`h-1.5 rounded-full overflow-hidden ${
            darkMode ? "bg-black/20" : "bg-white/60"
          }`}>
            <div
              className={`h-full rounded-full transition-all duration-300 ${statusInfo.progressColor}`}
              style={{ width: `${device.progress || 0}%` }}
            />
          </div>
          <div className={`text-xs ${statusInfo.color} opacity-80`}>
            {device.progress || 0}%
          </div>
        </div>
      )}
    </div>
  )}
		  
{editingBalance === device.deviceID ? (
  <div ref={dropdownRef} className="flex items-center space-x-2" key={`balance-edit-${device.deviceID}`}>
    <motion.div 
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
    >
      <div className="flex-shrink-0">
        <input
          type="number"
          value={newBalance}
          onChange={(e) => setNewBalance(e.target.value)}
          onClick={(e) => e.stopPropagation()} // 阻止事件冒泡
          className={`w-16 px-3 py-1 text-sm rounded-lg border-2 transition-all duration-200 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            darkMode
              ? "bg-[#1E1E1E] text-[#FFFFFF] border-[#404040] focus:border-[#FFFFFF]"
              : "bg-white text-[#1E1E1E] border-[#1E1E1E] focus:border-[#404040]"
          } ${
            ['alipaying', 'ubering'].includes(device.deviceStatus)
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:border-opacity-75'
          }`}
          step="0.01"
          disabled={['alipaying', 'ubering'].includes(device.deviceStatus)}
        />
      </div>

      <motion.button
        initial={{ opacity: 0, rotate: -180, scale: 0.9 }}
        animate={{ opacity: 1, rotate: 0, scale: 1 }}
        transition={{ 
          duration: 0.1,
          delay: 0.1,
          ease: "easeOut"
        }}
        onClick={(e) => {
          e.stopPropagation(); // 阻止事件冒泡
          if (!['alipaying', 'ubering'].includes(device.deviceStatus)) {
            handleBalanceChange(device.deviceID)
          }
        }}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
          ['alipaying', 'ubering'].includes(device.deviceStatus)
            ? darkMode
              ? 'bg-[#2C2C2C] text-[#666666] cursor-not-allowed'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : parseFloat(device.balance) === 0
              ? darkMode
                ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50 cursor-pointer'
                : 'bg-green-100 text-green-600 hover:bg-green-200 cursor-pointer'
              : darkMode
                ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50 cursor-pointer'
                : 'bg-green-100 text-green-600 hover:bg-green-200 cursor-pointer'
        }`}
        whileHover={!['alipaying', 'ubering'].includes(device.deviceStatus) ? {
          scale: 1.05,
          transition: { duration: 0.2 }
        } : {}}
        whileTap={!['alipaying', 'ubering'].includes(device.deviceStatus) ? {
          scale: 0.95,
          transition: { duration: 0.2 }
        } : {}}
      >
        <motion.i 
          className="fas fa-check"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            duration: 0.1,
            delay: 0.1,
            ease: "backOut"
          }}
        />
      </motion.button>
    </motion.div>
  </div>
) : (
  <motion.div
    onClick={() => {
      if (!['alipaying', 'ubering'].includes(device.deviceStatus)) {
        handleEditBalance(device.deviceID, device.balance)
      }
    }}
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
    className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all duration-200 ${
      ['alipaying', 'ubering'].includes(device.deviceStatus)
        ? darkMode
          ? 'bg-[#2C2C2C] text-[#666666] cursor-not-allowed'
          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        : parseFloat(device.balance) === 0
          ? darkMode
            ? 'bg-gray-900/30 text-gray-400 hover:bg-gray-700/50 cursor-pointer'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer'
          : darkMode
            ? 'bg-green-900/30 text-green-400 hover:bg-green-700/50 cursor-pointer'
            : 'bg-green-100 text-green-600 hover:bg-green-200 cursor-pointer'
    }`}
    whileHover={!['alipaying', 'ubering'].includes(device.deviceStatus) ? {
      scale: 1.02,
      transition: { duration: 0.2 }
    } : {}}
    whileTap={!['alipaying', 'ubering'].includes(device.deviceStatus) ? {
      scale: 0.98,
      transition: { duration: 0.2 }
    } : {}}
  >
    <motion.i 
	  data-exclude-reset="true"
      className="fas fa-wallet"
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ 
        duration: 0.3,
        delay: 0.1,
        ease: "backOut"
      }}
    />
    <motion.span 
      className="text-sm font-medium"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ 
        duration: 0.3,
        delay: 0.2,
        ease: "easeOut"
      }}
    >
      {parseFloat(device.balance).toFixed(2)}
    </motion.span>
  </motion.div>
)}


		  
		  
        </div>

        {/* 操作按鈕 */}
        <div className="flex items-center space-x-2">
{isResetting ? (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    className={`w-full text-sm group relative px-3 py-2 rounded-lg transition-all duration-200 ${
      darkMode 
        ? 'bg-yellow-900 text-yellow-400 hover:bg-yellow-800' 
        : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
    }`}
    onClick={() => handleDeviceAction('stopDevice', device.deviceID, device.deviceName)}
  >
    強制停止
  </motion.button>
) : (
  <>
    <motion.button
      whileHover={device.deviceStatus !== 'inactive' ? { scale: 1.05 } : {}}
      whileTap={device.deviceStatus !== 'inactive' ? { scale: 0.95 } : {}}
      disabled={device.deviceStatus === 'inactive'}
      className={`flex-1 text-sm px-3 py-2 rounded-lg transition-all duration-200 ${
        darkMode 
          ? device.deviceStatus === 'inactive'
            ? 'bg-[#2C2C2C] cursor-not-allowed text-[#666666]'
            : 'bg-[#404040] hover:bg-[#2C2C2C] text-white'
          : device.deviceStatus === 'inactive'
            ? 'bg-gray-400 cursor-not-allowed text-gray-300'
            : 'bg-gray-600 hover:bg-gray-400 text-white'
      }`}
      onClick={() => device.deviceStatus !== 'inactive' && handleModalOpen('uberDevice', device.deviceID, device.deviceName)}
    >
      重設 Uber
    </motion.button>

    <motion.button
      whileHover={device.deviceStatus !== 'inactive' ? { scale: 1.05 } : {}}
      whileTap={device.deviceStatus !== 'inactive' ? { scale: 0.95 } : {}}
      disabled={device.deviceStatus === 'inactive'}
      className={`flex-1 text-sm px-3 py-2 rounded-lg transition-all duration-200 ${
        darkMode 
          ? device.deviceStatus === 'inactive'
            ? 'bg-[#2C2C2C] cursor-not-allowed text-[#666666]'
            : 'bg-[#404040] hover:bg-[#2C2C2C] text-white'
          : device.deviceStatus === 'inactive'
            ? 'bg-gray-400 cursor-not-allowed text-gray-300'
            : 'bg-gray-600 hover:bg-gray-400 text-white'
      }`}
      onClick={() => device.deviceStatus !== 'inactive' && handleModalOpen('resetDevice', device.deviceID, device.deviceName)}
    >
      自動重設
    </motion.button>

    <motion.button
      whileHover={device.deviceStatus !== 'inactive' ? { scale: 1.05 } : {}}
      whileTap={device.deviceStatus !== 'inactive' ? { scale: 0.95 } : {}}
      disabled={device.deviceStatus === 'inactive'}
      className={`flex-1 text-sm px-3 py-2 rounded-lg transition-all duration-200 ${
        darkMode 
          ? device.deviceStatus === 'inactive'
            ? 'bg-[#2C2C2C] cursor-not-allowed text-[#666666]'
            : 'bg-[#404040] hover:bg-[#2C2C2C] text-white'
          : device.deviceStatus === 'inactive'
            ? 'bg-gray-400 cursor-not-allowed text-gray-300'
            : 'bg-gray-600 hover:bg-gray-400 text-white'
      }`}
      onClick={() => device.deviceStatus !== 'inactive' && handleModalOpen('buttonDevice', device.deviceID, device.deviceName)}
    >
      支付寶密碼
    </motion.button>
  </>
)}

        </div>
      </motion.div>
    );
  })}
</div>
    </AnimatePresence>
	
	
	</>
  );
  
}

export default Device;
